import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "git",
  name: "Git",
  nameEn: "Git",
  seo: {
    title: "Gitを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Gitはチーム開発に必須のバージョン管理ツール。GitHubの使い方からチーム開発ワークフローまで、ゆめさくエンジニアで実践的に学べます。",
    url: "/columns/git-programming-school",
  },
  hero: {
    title: "Gitを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Git",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Git" },
  ],
  overview: {
    heading: "Gitとは？",
    paragraphs: [
      "Gitは、ソースコードの変更履歴を管理する「バージョン管理システム」です。2005年にLinuxの生みの親であるリーナス・トーバルズによって開発され、現在では世界中のソフトウェア開発の標準ツールとなっています。",
      "コードの変更をいつでも過去の状態に戻せる、複数人が同時に同じプロジェクトを編集できる、変更の履歴を詳細に追跡できるなど、開発を安全かつ効率的に進めるための機能を備えています。",
      "GitHub・GitLab・Bitbucketなどのプラットフォームと組み合わせて使うことで、コードの共有・レビュー・デプロイまでの開発フロー全体を管理できます。ポートフォリオの公開にも欠かせないツールです。",
    ],
    useCases: [
      {
        label: "バージョン管理",
        description:
          "コードの変更履歴を記録し、いつでも過去の状態に戻せます。「あのとき動いていたコード」に確実に戻れる安心感があります。",
      },
      {
        label: "チーム開発",
        description:
          "ブランチ機能で複数の機能を並行開発し、プルリクエストでコードレビューを行う、実務の開発フローを支えます。",
      },
      {
        label: "GitHubによるポートフォリオ公開",
        description:
          "GitHubに自分のプロジェクトを公開することで、エンジニアとしてのスキルや活動を可視化し、就職活動で強力なアピール材料になります。",
      },
      {
        label: "CI/CDパイプライン",
        description:
          "GitHub Actionsなどを使って、コードのプッシュをトリガーにテスト・ビルド・デプロイを自動化できます。",
      },
    ],
  },
  whyLearn: {
    heading: "Gitを学ぶべき3つの理由",
    items: [
      {
        title: "エンジニアの全職種で必須のツール",
        description:
          "フロントエンド、バックエンド、データサイエンス、インフラなど、どの分野のエンジニアもGitを日常的に使います。Gitが使えないとチーム開発に参加できないため、最も基本的な必須スキルの一つです。",
      },
      {
        title: "GitHubが就職活動のポートフォリオになる",
        description:
          "GitHubのプロフィールは「エンジニアの名刺」とも言われます。日々の学習記録やプロジェクトをGitHubで公開することで、技術力をアピールでき、採用担当者の目に留まりやすくなります。",
      },
      {
        title: "チーム開発のワークフローを理解できる",
        description:
          "ブランチ戦略、プルリクエスト、コードレビューなど、実務の開発フローを理解するにはGitの知識が不可欠です。Gitを使いこなせることは、チームで働ける即戦力の証明になります。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでGitを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、カリキュラムの早い段階からGitを導入し、すべてのプロジェクトでGitを使った開発フローを実践します。「使い方を覚える」だけでなく、「チーム開発で自然に使いこなせる」レベルを目指します。",
      "実践プロジェクトではGitHubでのプルリクエストやコードレビューを経験し、現場のワークフローを体感できます。",
    ],
    topics: [
      "Gitの基本概念（リポジトリ・コミット・ブランチ）",
      "基本操作（add・commit・push・pull）",
      "ブランチの作成・マージ・コンフリクト解消",
      "GitHubの活用（プルリクエスト・Issues・README）",
      "チーム開発ワークフロー（Git Flow・GitHub Flow）",
      "実践：チーム開発プロジェクトでのGit運用",
    ],
  },
  careerPaths: {
    heading: "Gitを活かせるキャリア",
    items: [
      {
        title: "すべてのエンジニア職種で必須",
        description:
          "Gitは特定の職種のためのスキルではなく、フロントエンド・バックエンド・インフラ・データサイエンスなど、すべてのエンジニア職種で日常的に使用するツールです。Gitの習熟度が高いことは、即戦力として評価されるポイントになります。",
      },
      {
        title: "DevOps / プラットフォームエンジニア",
        description:
          "GitHubActionsなどのCI/CDパイプラインの構築・運用を担当する職種では、Gitの深い知識が求められます。開発フロー全体の効率化を担う重要な役割です。",
        salary: "500〜900万円",
      },
      {
        title: "テックリード / エンジニアリングマネージャー",
        description:
          "チームのブランチ戦略やコードレビューのルール設計など、Git運用の方針を決定する立場です。Gitを深く理解していることがリーダーシップを発揮するための基盤になります。",
        salary: "600〜1,000万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "1〜3ヶ月（基本コマンドのみ）",
        luagate: "2週間で基礎、以降は実践で定着",
      },
      {
        label: "挫折率",
        selfStudy: "コンフリクト解消やrebaseで混乱しやすい",
        luagate: "チーム開発の中で自然にトラブル対処を習得",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "一人でcommitするだけでチーム運用を経験できない",
        luagate: "プルリクエスト・レビューを実際のチームで体験",
      },
      {
        label: "質問対応",
        selfStudy: "「git resetとrevertの違い」など概念が混乱する",
        luagate: "メンターが図解でGitの仕組みを丁寧に説明",
      },
      {
        label: "就職サポート",
        selfStudy: "GitHubプロフィールの見せ方がわからない",
        luagate: "採用担当に刺さるGitHubポートフォリオを一緒に構築",
      },
    ],
  },
  faq: [
    {
      question: "GitとGitHubは何が違いますか？",
      answer:
        "Gitはバージョン管理ツール（ソフトウェア）で、GitHubはGitを使ったコード共有のWebサービスです。Gitはローカルで動作し、GitHubはオンラインでコードを共有・管理するプラットフォームの役割を果たします。",
    },
    {
      question: "Gitは最初に学ぶべきですか？",
      answer:
        "HTML/CSSやプログラミング言語と同時に学び始めるのがおすすめです。最初から使うことで、コードの変更管理を習慣化でき、後から学ぶよりもスムーズに定着します。ゆめさくエンジニアでもカリキュラム序盤からGitを導入しています。",
    },
    {
      question: "Gitのコマンドはたくさん覚える必要がありますか？",
      answer:
        "日常的に使うコマンドは10個程度です。add、commit、push、pull、branch、merge、checkout、statusなどの基本操作を覚えれば、チーム開発に十分参加できます。高度なコマンドは必要な場面で覚えていけば問題ありません。",
    },
    {
      question: "GUIツールを使ってもいいですか？",
      answer:
        "VSCodeのGit機能やSourceTreeなどのGUIツールは便利ですが、まずはコマンドラインで基本操作を理解しておくことをおすすめします。仕組みを理解したうえでGUIを使うと、トラブル時にも対処できるようになります。",
    },
  ],
  relatedLanguages: [
    { slug: "javascript", name: "JavaScript" },
    { slug: "python", name: "Python" },
    { slug: "typescript", name: "TypeScript" },
    { slug: "react", name: "React" },
  ],
};
